import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '../../lib/api'
import type { AdminRequest } from '../../lib/adminApi'
import { formatTimestamp } from '../../lib/datetime'
import type { AdminDraft, AdminDraftsResponse, CommissionerLinkResponse } from '../../types/protocol'

const STATUSES = ['setup', 'live', 'paused', 'complete'] as const

interface Props {
  request: AdminRequest
  onChanged: () => void
}

export default function DraftsTable({ request, onChanged }: Props) {
  const [status, setStatus] = useState('')
  const [drafts, setDrafts] = useState<AdminDraft[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Commissioner links are fetched one draft at a time and shown only for the
  // row that asked, so the list itself never carries a token.
  const [link, setLink] = useState<{ code: string; url: string; copied: boolean } | null>(null)
  const [deleting, setDeleting] = useState<{ code: string; confirm: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const query = status ? `?status=${status}` : ''
      const response = await request<AdminDraftsResponse>(`/admin/drafts${query}`)
      setDrafts(response.drafts)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load drafts')
    } finally {
      setLoading(false)
    }
  }, [request, status])

  useEffect(() => {
    void load()
  }, [load])

  async function copyLink(draft: AdminDraft) {
    setError(null)
    try {
      const { url } = await request<CommissionerLinkResponse>(
        `/admin/drafts/${draft.join_code}/commissioner_link`,
      )
      const absolute = `${window.location.origin}${url}`
      // clipboard.writeText needs a secure context, which a LAN http:// host
      // isn't — fall back to showing the URL for a manual copy.
      let copied = false
      try {
        await navigator.clipboard.writeText(absolute)
        copied = true
      } catch {
        copied = false
      }
      setLink({ code: draft.join_code, url: absolute, copied })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not fetch the commissioner link')
    }
  }

  async function destroy(draft: AdminDraft, confirmation: string) {
    setBusy(true)
    setError(null)
    try {
      await request(`/admin/drafts/${draft.join_code}?confirm=${encodeURIComponent(confirmation)}`, {
        method: 'DELETE',
      })
      setDeleting(null)
      setLink(null)
      await load()
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not delete the draft')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="panel p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="eyebrow">Drafts ({drafts.length})</h2>
        <label className="text-xs uppercase tracking-wide text-ink-lo">
          Status
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="ml-2 rounded border border-line-strong bg-ink px-3 py-1.5 text-sm normal-case tracking-normal text-ink-hi"
          >
            <option value="">All</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="mt-3 text-sm text-crit">{error}</p>}
      {loading && <p className="mt-3 text-sm text-ink-lo">Loading…</p>}
      {!loading && drafts.length === 0 && <p className="mt-3 text-sm text-ink-lo">No drafts yet.</p>}

      <ul className="mt-4 space-y-2">
        {drafts.map((draft) => (
          <li key={draft.id} className="rounded-lg border border-line bg-ink p-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-sm font-bold tracking-widest text-signal">
                {draft.join_code}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{draft.name}</p>
                <p className="text-xs text-ink-lo">
                  {draft.status} · {draft.team_count} teams · {draft.picks_made}/{draft.picks_total} picks
                  · created {formatTimestamp(draft.created_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void copyLink(draft)}
                className="text-xs text-ink-mid underline-offset-2 hover:underline"
              >
                Copy commissioner link
              </button>
              <button
                type="button"
                onClick={() => setDeleting({ code: draft.join_code, confirm: '' })}
                className="text-xs text-crit underline-offset-2 hover:underline"
              >
                Delete
              </button>
            </div>

            {link?.code === draft.join_code && (
              <div className="mt-3 rounded border border-line bg-surface-1 p-2">
                <p className="text-xs text-ink-mid">
                  {link.copied ? 'Copied to the clipboard.' : 'Copy this link — it is the commissioner credential.'}
                </p>
                <input
                  readOnly
                  value={link.url}
                  onFocus={(event) => event.target.select()}
                  className="mt-1 w-full rounded border border-line-strong bg-ink px-2 py-1 font-mono text-xs text-ink-hi"
                />
              </div>
            )}

            {deleting?.code === draft.join_code && (
              <form
                onSubmit={(event) => {
                  event.preventDefault()
                  void destroy(draft, deleting.confirm)
                }}
                className="mt-3 flex flex-wrap items-center gap-2 rounded border border-crit/40 bg-crit/5 p-2"
              >
                <p className="text-xs text-crit">
                  Deleting removes the draft, its teams and every pick. Type{' '}
                  <span className="font-mono font-bold">{draft.join_code}</span> to confirm.
                </p>
                <input
                  value={deleting.confirm}
                  onChange={(event) => setDeleting({ code: draft.join_code, confirm: event.target.value })}
                  autoFocus
                  className="w-24 rounded border border-line-strong bg-ink px-2 py-1 text-center font-mono text-sm uppercase"
                />
                <button
                  type="submit"
                  disabled={busy || deleting.confirm.trim().toUpperCase() !== draft.join_code}
                  className="rounded bg-crit px-3 py-1.5 text-xs font-semibold text-ink disabled:opacity-50"
                >
                  Delete draft
                </button>
                <button
                  type="button"
                  onClick={() => setDeleting(null)}
                  className="rounded border border-line-strong px-3 py-1.5 text-xs"
                >
                  Cancel
                </button>
              </form>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
